import { defineStore } from 'pinia';
import { ref } from 'vue';

import { HotelIngredient } from '@apps/main/modules/recipe/ingredients';
import type { DestinationAutocompleteType } from '@apps/main/new-desktop/modules/autoComplete';
import type { EditTripSegment } from '@apps/main/new-desktop/modules/editTripModal';

import {
  isDestinationAutoCompleteSuggestion,
  isHotelAutoCompleteSuggestion,
} from '@libs/trip-planner-interfaces';
import type {
  HeartedHotel,
  AutoCompleteSuggestion,
  IngredientId,
} from '@libs/trip-planner-interfaces';

import { useTripOverviewPageStore } from '../../pages/tripOverview/stores';
import { getSearchTerm } from '../helpers/searchTerm.helper';

import { useRecipeStore } from './recipe/recipeStore';
import { useStatsigEventsStore } from './statsigEvents/statsigEventsStore';

export const useDestinationSelectorStore = defineStore(
  'DestinationSelectorStore',
  () => {
    const recipeStore = useRecipeStore();
    const tripOverviewPageStore = useTripOverviewPageStore();
    const statsigEventsStore = useStatsigEventsStore();

    // State
    const searchText = ref<string>('');
    const autocompleteType = ref<DestinationAutocompleteType | undefined>(
      undefined
    );
    const editingIngredientId = ref<IngredientId | undefined>(undefined);

    // Actions
    function setDestinationSelectorData(
      newSearchText: string,
      ingredientId: IngredientId | undefined,
      modalType: DestinationAutocompleteType
    ): void {
      searchText.value = newSearchText;
      autocompleteType.value = modalType;
      editingIngredientId.value = ingredientId;
    }

    function handleConfirmSelection(
      newDestination: AutoCompleteSuggestion,
      isCarTrip?: boolean
    ): void {
      if (editingIngredientId.value) {
        updateDestination(newDestination, editingIngredientId.value);
      } else {
        addNewDestination(newDestination, isCarTrip);
      }
      clearDestinationSelectorData();
    }

    function handleOwnStayConfirmSelection(
      newDestination: AutoCompleteSuggestion,
      ownStayIngredientId: IngredientId
    ): void {
      const polygonId = isDestinationAutoCompleteSuggestion(newDestination)
        ? newDestination.polygonId
        : undefined;

      const searchTerm = getSearchTerm({
        polygonId,
        freeText: newDestination.label,
      });

      tripOverviewPageStore.setDestinationTextToOwnStayIngredient(
        ownStayIngredientId,
        searchTerm
      );
      clearDestinationSelectorData();
    }

    function changeDestinationByIngredientId(
      newDestination: AutoCompleteSuggestion,
      ingredientId: IngredientId
    ): void {
      updateDestination(newDestination, ingredientId);
    }

    function clearDestinationSelectorData(): void {
      searchText.value = '';
      autocompleteType.value = undefined;
      editingIngredientId.value = undefined;
    }

    function updateDestination(
      newDestination: AutoCompleteSuggestion,
      ingredientId: IngredientId
    ): void {
      let polygonId: number | undefined = undefined;

      if (isDestinationAutoCompleteSuggestion(newDestination)) {
        polygonId = newDestination.polygonId;
      }

      if (isHotelAutoCompleteSuggestion(newDestination)) {
        polygonId = newDestination.hotel.mainPolygonId;
      }

      const searchTerm = getSearchTerm({
        polygonId,
        freeText: newDestination.label,
      });

      recipeStore.updateHotelIngredientById(ingredientId, searchTerm);
      if (isHotelAutoCompleteSuggestion(newDestination)) {
        const heartedHotel: HeartedHotel = {
          code: newDestination.hotel.hotelCode,
        };
        recipeStore.updateHotelHeartedHotel(ingredientId, heartedHotel);
      }
    }

    function addNewDestination(
      newDestination: AutoCompleteSuggestion,
      isCarTrip?: boolean
    ): void {
      let polygonId: number | undefined = undefined;

      if (isDestinationAutoCompleteSuggestion(newDestination)) {
        polygonId = newDestination.polygonId;
      }

      if (isHotelAutoCompleteSuggestion(newDestination)) {
        polygonId = newDestination.hotel.mainPolygonId;
      }

      const searchTerm = getSearchTerm({
        polygonId,
        freeText: newDestination.label,
      });

      const newHotelIngredient = new HotelIngredient(searchTerm, true);

      if (isHotelAutoCompleteSuggestion(newDestination)) {
        const heartedHotel: HeartedHotel = {
          code: newDestination.hotel.hotelCode,
        };
        newHotelIngredient.heartedHotels = [heartedHotel];
      }

      if (isCarTrip) {
        recipeStore.addCarTripIngredient(newHotelIngredient);
        return;
      }
      recipeStore.addIngredient(newHotelIngredient);
    }

    function removeDestination(segment: EditTripSegment): void {
      statsigEventsStore.logTripOverviewRemoveDestination();
      recipeStore.deleteIngredientById(segment.ingredientId);
    }

    return {
      // State
      searchText,
      autocompleteType,

      // Actions
      changeDestinationByIngredientId,
      clearDestinationSelectorData,
      handleConfirmSelection,
      handleOwnStayConfirmSelection,
      setDestinationSelectorData,
      removeDestination,
    };
  }
);
